import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { acceleratedRaycast } from "three-mesh-bvh";
import { useActiveRenderConfig } from "../app/renderConfig.ts";
import { useStore } from "../app/store.ts";
import type { ModifierOverride, ViewMode } from "../app/storeTypes.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import { isVisibleInWorkspace } from "../app/visibilityPolicy.ts";
import type { BlendImage } from "../blend-reader/types/BlendImage.ts";
import type { BlendMaterial } from "../blend-reader/types/BlendMaterial.ts";
import type { MaterialGroup } from "../blend-reader/types/BlendMesh.ts";
import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import {
  buildSharedVertexIndices,
  expandForRendering,
  expandMorphDeltas,
  perVertexUVsFromLoopUVs,
} from "../modeling/loopExpand.ts";
import { identityProvenance } from "../modeling/provenance.ts";
import type { RenderTriangleIndices, SceneObject } from "../modeling/types.ts";
import {
  DEFAULT_MATERIAL,
  isMannequinObject,
  type LoopNormals,
} from "../modeling/types.ts";
import { useAsyncBoundsTree } from "./asyncBoundsTree.ts";
import { computeWeldedNormals } from "./computeWeldedNormals.ts";
import {
  createViewportMaterial,
  type TextureSlots,
} from "./createViewportMaterial.ts";
import {
  HIDDEN_SELECTION_GHOST_MAT,
  HIDDEN_SELECTION_GHOST_RENDER_ORDER,
  shouldShowHiddenSelectionGhost,
} from "./hiddenSelectionGhost.ts";
import { useDispose } from "./hooks/useDispose.ts";
import {
  type InkingIncludeCategory,
  markInkingCategory,
} from "./inkingCategories.ts";
import { useMannequinSurfaceSnapshots } from "./mannequinSurfaceSnapshots.ts";
import { getMaxAnisotropy } from "./materialCache.ts";
import { buildModifierEvaluationRequest } from "./modifierApply.ts";
import { refCacheKey } from "./modifiers/cacheKeys.ts";
import { modifierStackHasEnabledInstance } from "./modifiers/evaluation.ts";
import type {
  GeometryGroup,
  ModifierGeometry,
} from "./modifiers/modifierTypes.ts";
import { finalizeSharedVertex } from "./modifiers/renderGeometry.ts";
import { useModifierEvaluation } from "./modifiers/useModifierEvaluation.ts";
import {
  getActiveSelectedObjectIdFromState,
  getSelectionPrepassMaterial,
  useSelectionOutlineLayer,
} from "./selectionOutlinePass.ts";
import {
  noRaycast,
  SCULPT_DIM_MAT,
  WIRE_OVERLAY_MAT,
  WIRE_OVERLAY_XRAY_MAT,
  WIREFRAME_DEPTH_PREPASS_MAT,
} from "./sharedMaterials.ts";
import { useObjectTap } from "./useObjectTap.ts";
import { viewportSupportsSceneInteraction } from "./viewportInteractionPolicy.ts";

// ---------------------------------------------------------------------------
// Texture loading with correct color space per role
// ---------------------------------------------------------------------------

/** Cap real-time preview textures to 2048 px on their longest side. */
const MAX_TEXTURE_SIZE = 2048;

/**
 * Lazy texture entry — decodes the image once, then creates color-space
 * variants on demand. Only the variants actually requested by materials are
 * allocated, halving GPU memory for images used in a single role (e.g. a
 * normal map that never needs an sRGB variant).
 *
 * Images larger than {@link MAX_TEXTURE_SIZE} are downscaled during decode
 * so we never upload unnecessarily large textures for real-time preview.
 */
export class LazyTexturePair {
  private _srgb: THREE.Texture | null = null;
  private _linear: THREE.Texture | null = null;
  private _bitmap: ImageBitmap | null = null;

  constructor(packedData: Uint8Array) {
    // Decode the image off the main thread via createImageBitmap.
    // A single decode is shared by both color-space variants.
    const blob = new Blob([packedData as BlobPart]);
    createImageBitmap(blob, { imageOrientation: "none" })
      .then((bmp) => {
        const longest = Math.max(bmp.width, bmp.height);
        if (longest > MAX_TEXTURE_SIZE) {
          // Down-scale to fit within MAX_TEXTURE_SIZE, preserving aspect ratio.
          // The resize runs off-thread inside the browser's image pipeline.
          const scale = MAX_TEXTURE_SIZE / longest;
          const w = Math.round(bmp.width * scale);
          const h = Math.round(bmp.height * scale);
          return createImageBitmap(bmp, 0, 0, bmp.width, bmp.height, {
            resizeWidth: w,
            resizeHeight: h,
            resizeQuality: "high",
          }).then((resized) => {
            bmp.close(); // free the full-size bitmap
            return resized;
          });
        }
        return bmp;
      })
      .then((bmp) => {
        this._bitmap = bmp;
        // If any textures were created before the bitmap was ready, update them
        if (this._srgb) this._applyBitmap(this._srgb);
        if (this._linear) this._applyBitmap(this._linear);
      });
  }

  get srgb(): THREE.Texture {
    if (!this._srgb) this._srgb = this._makeTex(THREE.SRGBColorSpace);
    return this._srgb;
  }

  get linear(): THREE.Texture {
    if (!this._linear) this._linear = this._makeTex(THREE.LinearSRGBColorSpace);
    return this._linear;
  }

  dispose(): void {
    this._srgb?.dispose();
    this._linear?.dispose();
    this._bitmap?.close();
    this._srgb = null;
    this._linear = null;
    this._bitmap = null;
  }

  private _makeTex(colorSpace: THREE.ColorSpace): THREE.Texture {
    const tex = new THREE.Texture();
    tex.flipY = false;
    tex.colorSpace = colorSpace;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    // Enable anisotropic filtering for sharper textures at glancing angles
    tex.anisotropy = getMaxAnisotropy();
    if (this._bitmap) {
      this._applyBitmap(tex);
    }
    // Otherwise the bitmap is still loading — it will be applied when ready.
    return tex;
  }

  private _applyBitmap(tex: THREE.Texture): void {
    if (!this._bitmap) return;
    tex.image = this._bitmap;
    tex.needsUpdate = true;
  }
}

/**
 * Build a texture cache from packed images. Each entry decodes the image
 * once (off-thread) and creates sRGB / linear THREE.Texture variants lazily
 * on first access.
 */
export function buildTextureCache(
  images: Map<bigint, BlendImage>,
): Map<bigint, LazyTexturePair> {
  const cache = new Map<bigint, LazyTexturePair>();
  for (const [ptr, img] of images) {
    if (!img.packedData) continue;
    cache.set(ptr, new LazyTexturePair(img.packedData));
  }
  return cache;
}

/** Dispose all textures in a cache (call when the cache is being replaced). */
export function disposeTextureCache(cache: Map<bigint, LazyTexturePair>): void {
  for (const entry of cache.values()) entry.dispose();
  cache.clear();
}

export interface MeshNodeProps {
  name: string;
  /** Shared vertex positions. */
  positions: Float32Array;
  /** Per-loop normals (per face-corner). */
  normals: LoopNormals;
  /** Render-space triangle indices (corner-based). */
  indices: RenderTriangleIndices;
  uvs?: Float32Array;
  /** Polygon topology for expanding shared vertices to per-corner GPU arrays. */
  topology?: import("../modeling/types.ts").PolygonTopology;
  localPosition: [number, number, number];
  localRotation: [number, number, number];
  localScale: [number, number, number];
  /** Static morph target deltas — keyed by shape key name. Never changes after load. */
  morphKeys?: { name: string; deltas: Float32Array }[];
  /** Shape key set name — used to subscribe to weights from the store. */
  shapeKeySetRef?: string;
  materials: (BlendMaterial | null)[];
  materialGroups: MaterialGroup[];
  /** Textures resolved from material image pointers — sRGB and linear variants per image. */
  textureCache: Map<bigint, LazyTexturePair>;
  viewMode: ViewMode;
  modifiers: BlendModifier[];
  modifierSceneObjectsByName?: ReadonlyMap<string, SceneObject>;
  modifierOverrides?: ReadonlyMap<string, readonly ModifierOverride[]>;
  /** When set, tapping selects the entire blend file entry (monolithic). */
  blendEntryId?: string;
}

export function MeshNode({
  name,
  positions,
  normals,
  indices,
  uvs,
  topology,
  localPosition,
  localRotation,
  localScale,
  morphKeys,
  shapeKeySetRef,
  materials,
  materialGroups,
  textureCache,
  viewMode,
  modifiers,
  modifierSceneObjectsByName,
  modifierOverrides,
  blendEntryId,
}: MeshNodeProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const visible = useStore((s) => s.visibility.get(name) ?? true);
  const renderVisible = useStore((s) => s.renderVisibility.get(name) ?? true);
  const inkingCategory: InkingIncludeCategory = blendEntryId
    ? "blend"
    : "primitive";

  const selected = useSelectedLeafObjectIds().has(name);
  const activeSelectedObjectId = useStore(getActiveSelectedObjectIdFromState);
  const workspaceMode = useStore((s) => s.workspaceMode);
  const effectiveVisible = isVisibleInWorkspace({
    workspaceMode,
    viewportVisible: visible,
    renderVisible,
  });
  const showSceneInteraction = viewportSupportsSceneInteraction(workspaceMode);
  const showHiddenSelectionGhost = shouldShowHiddenSelectionGhost({
    selected,
    effectiveVisible,
    showSceneInteraction,
  });
  const isSculptTarget = useStore((s) => s.sculptMode?.objectId === name);
  const isSculptDimmed = useStore(
    (s) => s.sculptMode !== null && s.sculptMode.objectId !== name,
  );
  const flatShading = useStore((s) => s.shadingOverrides.get(name) === "flat");
  const showSelectionPrepass =
    selected && effectiveVisible && showSceneInteraction && !isSculptTarget;
  const selectionVariant = modifierStackHasEnabledInstance(modifiers)
    ? "instance"
    : "default";
  const selectionPrepassMaterial = useMemo(
    () =>
      getSelectionPrepassMaterial(
        name,
        selectionVariant,
        name === activeSelectedObjectId,
      ),
    [name, selectionVariant, activeSelectedObjectId],
  );
  const selectionPrepassRef = useRef<THREE.Mesh>(null);

  useEffect(() => {
    if (meshRef.current) markInkingCategory(meshRef.current, inkingCategory);
  }, [inkingCategory]);

  useSelectionOutlineLayer(selectionPrepassRef, showSelectionPrepass);

  // Tap → select (monolithic when part of a blend file entry)
  const { handlePointerDown, handlePointerUp } = useObjectTap(
    name,
    undefined,
    blendEntryId,
  );

  const hasEnabledModifiers = modifiers.some((m) => m.enabled);

  const inputModifierGeometry = useMemo<ModifierGeometry | null>(() => {
    if (!topology || !hasEnabledModifiers) return null;

    const vertexCount = positions.length / 3;
    const perVertexUVs =
      uvs && uvs.length > 0
        ? perVertexUVsFromLoopUVs(uvs, topology, vertexCount)
        : null;

    return {
      positions,
      indices: buildSharedVertexIndices(indices, topology),
      normals: null,
      uvs: perVertexUVs,
      morphArrays: morphKeys?.map((mk) => mk.deltas) ?? [],
      morphNames:
        morphKeys && morphKeys.length > 0
          ? morphKeys.map((mk) => mk.name)
          : undefined,
      morphTargetsRelative:
        morphKeys && morphKeys.length > 0 ? true : undefined,
      skinIndex: null,
      skinWeight: null,
      sculptDisplacement: null,
      groups: materialGroups as GeometryGroup[],
      edges: topology.edges.length > 0 ? topology.edges : null,
      edgeFlags: topology.edges.length > 0 ? topology.edgeFlags : null,
      skinRadii: null,
      topology,
      provenance: identityProvenance(vertexCount),
    };
  }, [
    topology,
    hasEnabledModifiers,
    positions,
    indices,
    uvs,
    morphKeys,
    materialGroups,
  ]);

  const mannequinSurfaceTargetIds = useMemo(() => {
    if (!modifierSceneObjectsByName) return [];
    const ids: string[] = [];
    for (const modifier of modifiers) {
      if (
        !modifier.enabled ||
        modifier.kind !== "shrinkwrap" ||
        !modifier.targetObjectName
      ) {
        continue;
      }
      const target = modifierSceneObjectsByName.get(modifier.targetObjectName);
      if (target && isMannequinObject(target)) ids.push(target.id);
    }
    return ids;
  }, [modifiers, modifierSceneObjectsByName]);
  const mannequinSurfaceSnapshots = useMannequinSurfaceSnapshots(
    mannequinSurfaceTargetIds,
  );
  const runtimeSourceGeometries = useMemo(() => {
    if (!modifierSceneObjectsByName || mannequinSurfaceSnapshots.size === 0) {
      return undefined;
    }
    const out: Record<string, ModifierGeometry> = {};
    for (const [objectName, target] of modifierSceneObjectsByName) {
      const snapshot = mannequinSurfaceSnapshots.get(target.id);
      if (snapshot) out[objectName] = snapshot.geometry;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }, [modifierSceneObjectsByName, mannequinSurfaceSnapshots]);
  const runtimeSourceCacheKeys = useMemo(() => {
    if (!modifierSceneObjectsByName || mannequinSurfaceSnapshots.size === 0) {
      return undefined;
    }
    const out = new Map<string, string>();
    for (const [objectName, target] of modifierSceneObjectsByName) {
      const snapshot = mannequinSurfaceSnapshots.get(target.id);
      if (snapshot) out.set(objectName, snapshot.cacheKey);
    }
    return out.size > 0 ? out : undefined;
  }, [modifierSceneObjectsByName, mannequinSurfaceSnapshots]);

  const modifierRequest = useMemo(() => {
    if (!inputModifierGeometry) return null;
    return buildModifierEvaluationRequest({
      baseCacheKey: refCacheKey("blend-mesh", positions),
      geometry: inputModifierGeometry,
      owner: {
        position: localPosition,
        rotation: localRotation,
        scale: localScale,
      },
      modifiers,
      sceneObjectsByName: modifierSceneObjectsByName,
      runtimeSourceGeometries,
      runtimeSourceCacheKeys,
      modifierOverrides,
    });
  }, [
    inputModifierGeometry,
    positions,
    localPosition,
    localRotation,
    localScale,
    modifiers,
    modifierSceneObjectsByName,
    runtimeSourceGeometries,
    runtimeSourceCacheKeys,
    modifierOverrides,
  ]);

  const evaluatedModifierGeometry = useModifierEvaluation(modifierRequest);

  // Geometry depends only on structural data (positions, normals, indices,
  // morph deltas, material groups, modifiers). Shape key *weights* are applied
  // via morphTargetInfluences — a cheap array update that doesn't rebuild geometry.
  const geometry = useMemo(() => {
    let geo: THREE.BufferGeometry;

    // Pre-compute morph delta arrays in the same layout the modifier pipeline
    // expects (per-vertex stride 3).  Used by both the with-modifiers and
    // no-modifiers paths.
    const morphNamesList: string[] = [];
    const morphArraysShared: Float32Array[] = [];
    const morphArraysExpanded: Float32Array[] = [];
    if (morphKeys && morphKeys.length > 0) {
      for (const mk of morphKeys) {
        morphNamesList.push(mk.name);
        morphArraysShared.push(mk.deltas);
        if (topology)
          morphArraysExpanded.push(expandMorphDeltas(mk.deltas, topology));
      }
    }

    if (topology && hasEnabledModifiers && evaluatedModifierGeometry) {
      geo = finalizeSharedVertex(evaluatedModifierGeometry);
      if (!evaluatedModifierGeometry.normals) computeWeldedNormals(geo);
    } else if (topology) {
      // Also serves as the cold-start fallback when modifiers are enabled but
      // the worker has not yet produced its first authoritative result —
      // render the base mesh rather than flashing an empty geometry.
      // `useModifierEvaluation` handles stale-while-revalidate on re-eval.
      const expanded = expandForRendering(positions, normals, topology, uvs);
      geo = new THREE.BufferGeometry();
      geo.setAttribute(
        "position",
        new THREE.BufferAttribute(expanded.renderVertices, 3),
      );
      geo.setAttribute(
        "normal",
        new THREE.BufferAttribute(expanded.renderNormals, 3),
      );
      if (expanded.renderUVs) {
        geo.setAttribute(
          "uv",
          new THREE.BufferAttribute(expanded.renderUVs, 2),
        );
      }
      if (indices.length > 0) {
        geo.setIndex(new THREE.BufferAttribute(indices, 1));
      }
      // With per-corner expansion, morph deltas need matching per-corner layout.
      if (morphArraysExpanded.length > 0) {
        geo.morphAttributes.position = morphArraysExpanded.map(
          (arr) => new THREE.BufferAttribute(arr, 3),
        );
        geo.morphTargetsRelative = true;
        (geo.userData as Record<string, unknown>).morphNames = morphNamesList;
      }
      for (const group of materialGroups) {
        geo.addGroup(group.start, group.count, group.materialIndex);
      }
    } else {
      geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geo.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
      if (uvs) {
        geo.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
      }
      if (indices.length > 0) {
        geo.setIndex(new THREE.BufferAttribute(indices, 1));
      }
      if (morphArraysShared.length > 0) {
        geo.morphAttributes.position = morphArraysShared.map(
          (arr) => new THREE.BufferAttribute(arr, 3),
        );
        geo.morphTargetsRelative = true;
        (geo.userData as Record<string, unknown>).morphNames = morphNamesList;
      }
      for (const group of materialGroups) {
        geo.addGroup(group.start, group.count, group.materialIndex);
      }
    }

    geo.computeBoundingSphere();

    return geo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    positions,
    normals,
    indices,
    uvs,
    morphKeys,
    materialGroups,
    hasEnabledModifiers,
    evaluatedModifierGeometry,
    topology,
  ]);

  // Each geometryBundle rebuild allocates a fresh BufferGeometry (lines 329
  // and 356). Without this the prior GPU-backed vertex buffers never release
  // — any dep change (material groups, morph keys, modifier key, etc.) would
  // leak a geometry on every re-fire.
  useDispose(geometry);

  useAsyncBoundsTree(geometry, geometry);

  // Morph influences: subscribe directly to the store for this object's weights.
  // This is the only thing that changes during shape key scrubbing — geometry
  // stays stable, so Three.js handles the blending on the GPU.
  const morphWeights = useStore((s) =>
    shapeKeySetRef ? s.shapeKeyWeights.get(shapeKeySetRef) : undefined,
  );

  const morphInfluences = useMemo(() => {
    if (!morphKeys) return undefined;
    const names = (geometry.userData as Record<string, string[]>).morphNames;
    if (!names) return undefined;
    // Fall back to all-zero influences when the weights map is missing.
    // Without this, morphTargetInfluences stays empty while the geometry
    // has morphAttributes, producing invalid WGSL and a silent GPU failure.
    return names.map((n) => morphWeights?.get(n) ?? 0);
  }, [geometry, morphKeys, morphWeights]);

  const showWireframe = useActiveRenderConfig((c) => c.showWireframe);
  const objectXray = useStore((s) => s.objectXray);
  const wireframeXray = useActiveRenderConfig((c) => c.wireframeXray);
  const showTextures = useStore((s) => s.showTextures);
  const textureMapsEnabled = useStore((s) => s.textureMapsEnabled);
  const matcapPreset = useActiveRenderConfig((c) => c.matcapPreset);
  const shadowsEnabled = useActiveRenderConfig((c) => c.shadowsEnabled);
  useStore((s) => s.debugMaterial);
  useStore((s) => s.renderGridOverride);
  const objectXrayActive = workspaceMode === "object" && objectXray;

  // Track group presence separately so material memo doesn't depend on the
  // full geometry object (avoids needless material re-creation on modifier
  // recomputation that doesn't change group structure).
  const hasGroups = useMemo(() => geometry.groups.length > 0, [geometry]);

  const threeMaterials = useMemo(() => {
    const srgbTex = (ptr: bigint | undefined) =>
      ptr != null ? textureCache.get(ptr)?.srgb : undefined;
    const linearTex = (ptr: bigint | undefined) =>
      ptr != null ? textureCache.get(ptr)?.linear : undefined;

    const options = {
      viewMode,
      matcapPreset,
      objectXray: objectXrayActive,
      wireframeXray,
      showTextures,
      textureMapsEnabled,
    };

    const makeMaterial = (mat: BlendMaterial | null) => {
      const color = mat
        ? new THREE.Color(mat.baseColor[0], mat.baseColor[1], mat.baseColor[2])
        : DEFAULT_MATERIAL.color;

      let textures: TextureSlots | undefined;
      if (mat) {
        textures = {
          baseColor: srgbTex(mat.baseColorImagePtr),
          normal: linearTex(mat.normalImagePtr),
          roughness: linearTex(mat.roughnessImagePtr),
          metallic: linearTex(mat.metallicImagePtr),
          emission: srgbTex(mat.emissionImagePtr),
        };
      }

      return createViewportMaterial(
        {
          color,
          roughness: mat?.roughness ?? 0.5,
          metalness: mat?.metallic ?? 0.0,
          emissive: mat
            ? new THREE.Color(
                mat.emissionColor[0],
                mat.emissionColor[1],
                mat.emissionColor[2],
              )
            : undefined,
          emissiveIntensity: mat?.emissionStrength,
          opacity: mat?.alpha,
          specularIntensity: mat?.specular,
          clearcoat: mat?.clearcoat,
          clearcoatRoughness: mat?.clearcoatRoughness,
          sheen: mat?.sheen,
          sheenRoughness: mat?.sheenRoughness,
          sheenColor: mat
            ? new THREE.Color(
                mat.sheenTint[0],
                mat.sheenTint[1],
                mat.sheenTint[2],
              )
            : undefined,
          transmission: mat?.transmission,
          ior: mat?.ior,
          normalIntensity: mat?.normalStrength,
          flatShading,
          textures,
        },
        options,
      );
    };

    if (hasGroups && materials.length > 1) {
      return materials.map((mat) => makeMaterial(mat));
    }
    return makeMaterial(materials[0] ?? null);
  }, [
    materials,
    hasGroups,
    viewMode,
    objectXrayActive,
    wireframeXray,
    showTextures,
    textureMapsEnabled,
    textureCache,
    matcapPreset,
    flatShading,
  ]);

  // Own the lifetime of the materials allocated above. Three.js's dispose()
  // does not touch referenced textures, so disposing these does not affect
  // the LazyTexturePair instances held by the blend-file texture cache.
  useEffect(() => {
    return () => {
      if (Array.isArray(threeMaterials)) {
        for (const m of threeMaterials) m.dispose();
      } else {
        threeMaterials.dispose();
      }
    };
  }, [threeMaterials]);

  // Overlay visibility flags (materials are shared module-level constants)
  const showWireOverlay = showWireframe && viewMode !== "wireframe";
  const needsDepthPrepass = viewMode === "wireframe" && !wireframeXray;

  // noRaycast (from sharedMaterials) prevents Three.js from producing
  // intersections for overlay/hidden meshes.

  const meshElement = (
    <mesh
      ref={meshRef}
      name={name}
      visible={effectiveVisible}
      raycast={effectiveVisible ? acceleratedRaycast : noRaycast}
      position={localPosition}
      rotation={localRotation}
      scale={localScale}
      geometry={geometry}
      material={threeMaterials}
      userData={{ poserefObjectId: name, poserefInkingInclude: inkingCategory }}
      morphTargetInfluences={morphInfluences}
      castShadow={shadowsEnabled}
      receiveShadow={shadowsEnabled}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    />
  );

  return (
    <>
      {needsDepthPrepass && (
        <mesh
          visible={effectiveVisible}
          raycast={noRaycast}
          position={localPosition}
          rotation={localRotation}
          scale={localScale}
          geometry={geometry}
          material={WIREFRAME_DEPTH_PREPASS_MAT}
          morphTargetInfluences={morphInfluences}
          renderOrder={-1}
        />
      )}
      {meshElement}
      {showHiddenSelectionGhost && (
        <mesh
          raycast={noRaycast}
          position={localPosition}
          rotation={localRotation}
          scale={localScale}
          geometry={geometry}
          material={HIDDEN_SELECTION_GHOST_MAT}
          morphTargetInfluences={morphInfluences}
          renderOrder={HIDDEN_SELECTION_GHOST_RENDER_ORDER}
          userData={{ excludeFromExport: true }}
        />
      )}
      {showWireOverlay && (
        <mesh
          visible={effectiveVisible}
          raycast={noRaycast}
          position={localPosition}
          rotation={localRotation}
          scale={localScale}
          geometry={geometry}
          material={wireframeXray ? WIRE_OVERLAY_XRAY_MAT : WIRE_OVERLAY_MAT}
          morphTargetInfluences={morphInfluences}
          renderOrder={1}
        />
      )}
      {/* Sculpt dim overlay — darkens this object when another object is being sculpted */}
      {isSculptDimmed && (
        <mesh
          visible={effectiveVisible}
          raycast={noRaycast}
          position={localPosition}
          rotation={localRotation}
          scale={localScale}
          geometry={geometry}
          material={SCULPT_DIM_MAT}
          morphTargetInfluences={morphInfluences}
          renderOrder={3}
          userData={{ excludeFromExport: true }}
        />
      )}
      {showSelectionPrepass && (
        <mesh
          ref={selectionPrepassRef}
          visible={effectiveVisible}
          raycast={noRaycast}
          position={localPosition}
          rotation={localRotation}
          scale={localScale}
          geometry={geometry}
          material={selectionPrepassMaterial}
          morphTargetInfluences={morphInfluences}
          userData={{ excludeFromExport: true }}
        />
      )}
    </>
  );
}
